'use client';
import classes from './BlogCarousel.module.css';

import algoliasearch from 'algoliasearch';
import { useEffect, useState } from 'react';
import BlogCarouselItem from './BlogCarouselItem';
import Link from 'next/link';
import locations from '@/utils/locations';
import BlogCarouselSelection from './BlogCarouselSelection';
import { newFilters } from '@/utils/newFilters';

const client =
	process.env.NEXT_PUBLIC_OG_DB === 'true'
		? algoliasearch(
				process.env.NEXT_PUBLIC_ALGOLIA_APPID,
				process.env.NEXT_PUBLIC_ALGOLIA_API_KEY
		  )
		: algoliasearch(
				process.env.NEXT_PUBLIC_DUMMY_ALGOLIA_APPID,
				process.env.NEXT_PUBLIC_DUMMY_ALGOLIA_API_KEY
		  );

const cleanLinkHandler = (item) => {
	let link = item.replace(/ /g, '-').replace(/&/g, 'and').toLowerCase();
	return link;
};

const BlogCarousel = ({
	type,
	sellerId,
	category,
	option,
	subType,
	brand,
	style,
	region,
}) => {
	const [displayListings, setDisplayListings] = useState([]);
	const [displaySelections, setDisplaySelections] = useState([]);

	const generateLink = (category, option, subType, brand, style, region) => {
		let link = '/collections';
		const params = [];

		// 1. Handle when only category is present
		if (category && !option && !subType) {
			params.push(`category=${cleanLinkHandler(category)}`);
		}

		// 2. Handle when only option is present (find category)
		if (!category && option && !subType) {
			const categoryMatch = newFilters.categories.find((cat) =>
				cat.subCategories.some(
					(subCat) => subCat.id === cleanLinkHandler(option)
				)
			);

			if (categoryMatch) {
				params.push(`category=${cleanLinkHandler(categoryMatch.id)}`);
				params.push(`option=${cleanLinkHandler(option)}`);
			}
		}

		// 3. Handle when only subType is present (find option and category)
		if (!category && !option && subType) {
			const categoryAndOptionMatch = newFilters.categories.find((cat) =>
				cat.subCategories.some((subCat) =>
					subCat.types.some((type) => type.id === cleanLinkHandler(subType))
				)
			);

			if (categoryAndOptionMatch) {
				const matchingSubCategory = categoryAndOptionMatch.subCategories.find(
					(subCat) =>
						subCat.types.some((type) => type.id === cleanLinkHandler(subType))
				);

				if (matchingSubCategory) {
					params.push(
						`category=${cleanLinkHandler(categoryAndOptionMatch.id)}`
					);
					params.push(`option=${cleanLinkHandler(matchingSubCategory.id)}`);
					params.push(`subType=${cleanLinkHandler(subType)}`);
				}
			}
		}

		// 4. Handle other parameters (brand, style, region)
		if (brand) {
			params.push(`brand=${cleanLinkHandler(brand)}`);
		}
		if (style) {
			params.push(`style=${cleanLinkHandler(style)}`);
		}
		if (region) {
			params.push(`location=${cleanLinkHandler(region)}`);
		}

		// 5. Join the parameters to form the final link
		if (params.length > 0) {
			link += `?${params.join('&')}`;
		}

		console.log('link ', link);
		return link;
	};

	const capitalizeWords = (str) => {
		return str
			.split(' ')
			.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
			.join(' ');
	};

	const generateHeader = (
		category,
		option,
		subType,
		brand,
		style,
		region,
		type
	) => {
		let header = '';
		let title = '';
		let location = '';

		if (category) {
			title = `${category} ${
				category === 'seating' || category === 'storage' ? 'Furniture' : ''
			}`;
		}
		if (option) {
			title = option;
		}
		if (subType) {
			title = subType;
		}
		if (!category && !option && !subType) {
			title = 'Furniture';
		}
		if (region) {
			let theLoc = locations.find(
				(loc) => loc.id.toLowerCase() === region?.toLowerCase()
			);
			location = theLoc?.geolocation;
		}

		return (header = `${type !== 'selection-highlight' ? 'Popular' : ''} ${
			style ? capitalizeWords(style) : ''
		} ${brand ? capitalizeWords(brand) : ''} ${capitalizeWords(title)} ${
			location ? `in ${location}` : ''
		}  `.trim());
	};

	const getProducts = async (
		type,
		sellerId,
		category,
		option,
		subType,
		brand,
		style,
		region,
		limit,
		displayText,
		href
	) => {
		const index = client.initIndex('kashew_listings');
		await index
			.search(sellerId ? sellerId : '', {
				hitsPerPage: limit ? limit : 15,
				page: 0,
				filters: `status:active${
					category
						? ` AND newSchema.categories:"${category
								.replace(/-/g, ' ')
								.replace(/\band\b/g, '&')}"`
						: ''
				}${
					option
						? ` AND newSchema.subCategories:"${option
								.replace(/-/g, ' ')
								.replace(/\band\b/g, '&')}"`
						: ''
				}${
					subType
						? ` AND newSchema.types:"${subType
								.replace(/-/g, ' ')
								.replace(/\band\b/g, '&')}"`
						: ''
				}${
					brand
						? ` AND newBrand:"${brand
								.replace(/-/g, ' ')
								.replace(/\band\b/g, '&')}"`
						: ''
				}${
					style
						? ` AND velouAttributes.styles:"${style
								.replace(/-/g, ' ')
								.replace(/\band\b/g, '&')
								.replace(/\s+/g, '_')}"`
						: ''
				}${
					region
						? ` AND region:"${region
								.replace(/-/g, ' ')
								.replace(/\band\b/g, '&')}"`
						: ''
				}`,
				clickAnalytics: true,
			})
			.then(async (res) => {
				if (type === 'selection-highlight') {
					// Add displayText to each hit
					const hitsWithDisplayText = res.hits.map((hit) => ({
						...hit,
						displayText: displayText, // Set displayText to the current filter
						href: href,
					}));

					setDisplaySelections((prev) => [...prev, ...hitsWithDisplayText]);
				}

				setDisplayListings(res.hits);
			})
			.catch((err) => {
				console.log(err);
			});
	};

	useEffect(() => {
		if (type === 'store-highlight' && sellerId) {
			getProducts(
				(type = 'store-highlight'),
				(sellerId = sellerId),
				(category = category),
				(option = option),
				(subType = subType),
				(brand = brand),
				(style = style),
				(region = region),
				15
			);
		} else if (type === 'product-highlight') {
			getProducts(
				(type = 'product-highlight'),
				(sellerId = sellerId),
				(category = category),
				(option = option),
				(subType = subType),
				(brand = brand),
				(style = style),
				(region = region),
				15
			);
		} else if (
			type === 'selection-highlight' &&
			(sellerId || category || option || subType || brand || style || region)
		) {
			let filters = [category, option, subType, brand, style, region];
			const cleanFilters = filters.filter((filter) => filter);

			if (displaySelections.length === cleanFilters.length) {
				return;
			}
			for (let filter of cleanFilters) {
				let displayText = generateHeader(
					filter === category ? filter : null,
					filter === option ? filter : null,
					filter === subType ? filter : null,
					filter === brand ? filter : null,
					filter === style ? filter : null,
					filter === region ? filter : null,
					(type = 'selection-highlight')
				);

				let href = generateLink(
					filter === category ? filter : null,
					filter === option ? filter : null,
					filter === subType ? filter : null,
					filter === brand ? filter : null,
					filter === style ? filter : null,
					filter === region ? filter : null
				);

				getProducts(
					'selection-highlight',
					sellerId || null,
					filter === category ? filter : null,
					filter === option ? filter : null,
					filter === subType ? filter : null,
					filter === brand ? filter : null,
					filter === style ? filter : null,
					filter === region ? filter : null,
					1, // Limit per filter
					displayText,
					href
				);
			}
		}
	}, [type, sellerId, category, option, subType, brand, style, region]);

	return (
		<>
			{displayListings?.length > 0 ? (
				type === 'store-highlight' ? (
					<section className={classes.wrapper}>
						<div className={classes.justifyBetween}>
							<h2 className={classes.title}>This seller's highlights</h2>
							<Link href={`/profile/${sellerId}`} className={classes.link}>
								View All
							</Link>
						</div>
						<section className={classes.scrollWrapper}>
							<section className={classes.uWrapper}>
								<ul className={classes.list}>
									{displayListings?.map((listing, idx) => (
										<BlogCarouselItem listing={listing} idx={idx} />
									))}
								</ul>
							</section>
						</section>
					</section>
				) : type === 'product-highlight' ? (
					<section className={classes.wrapper}>
						<div className={classes.justifyBetween}>
							<h2 className={classes.title}>
								{generateHeader(
									category,
									option,
									subType,
									brand,
									style,
									region
								)}
							</h2>
							<Link
								href={generateLink(
									category,
									option,
									subType,
									brand,
									style,
									region
								)}
								className={classes.link}
							>
								View All
							</Link>
						</div>
						<section className={classes.scrollWrapper}>
							<section className={classes.uWrapper}>
								<ul className={classes.list}>
									{displayListings?.map((listing, idx) => (
										<BlogCarouselItem listing={listing} idx={idx} />
									))}
								</ul>
							</section>
						</section>
					</section>
				) : (
					type === 'selection-highlight' && (
						<>
							<section className={classes.wrapper}>
								<div className={classes.justifyBetween}>
									<h2 className={classes.title}>
										Curated Collections on Kashew
									</h2>
								</div>
								<section className={classes.scrollWrapper}>
									<section className={classes.uWrapperSmall}>
										<ul className={classes.listSmall}>
											{displaySelections?.map((listing, idx) => (
												<BlogCarouselSelection listing={listing} idx={idx} />
											))}
										</ul>
									</section>
								</section>
							</section>
						</>
					)
				)
			) : (
				''
			)}
		</>
	);
};

export default BlogCarousel;
